"use client";

import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { useI18n } from "@/lib/i18n/I18nProvider";

const FAQ_KEYS = [
  { question: "landing.faq.q1", answer: "landing.faq.a1" },
  { question: "landing.faq.q2", answer: "landing.faq.a2" },
  { question: "landing.faq.q3", answer: "landing.faq.a3" },
  { question: "landing.faq.q4", answer: "landing.faq.a4" },
  { question: "landing.faq.q5", answer: "landing.faq.a5" },
  { question: "landing.faq.q6", answer: "landing.faq.a6" },
  { question: "landing.faq.q7", answer: "landing.faq.a7" },
  { question: "landing.faq.q8", answer: "landing.faq.a8" },
];

interface FAQItemProps {
  item: {
    question: string;
    answer: string;
  };
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ item, isOpen, onToggle }: FAQItemProps) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden hover:border-slate-300 transition-colors">
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors"
      >
        <h4 className="text-lg font-semibold text-slate-900 text-left break-words">{item.question}</h4>
        <ChevronDown
          className={`w-5 h-5 text-slate-600 flex-shrink-0 ml-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="px-6 py-5 bg-slate-50 border-t border-slate-200">
          <p className="text-slate-700 leading-relaxed">{item.answer}</p>
        </div>
      )}
    </div>
  );
}

export function FAQSection() {
  const { t } = useI18n();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const FAQ_ITEMS = FAQ_KEYS.map((item) => ({
    question: t(item.question),
    answer: t(item.answer),
  }));

  // FAQPage JSON-LD for Google Rich Snippets
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };

  return (
    <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">{t("landing.faq.title")}</h2>
          <p className="text-lg text-slate-600">
            {t("landing.faq.subtitle")}
          </p>
        </div>

        <div className="space-y-4">
          {FAQ_ITEMS.map((item, index) => (
            <FAQItem
              key={index}
              item={item}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>

        {/* CTA after FAQ */}
        <div className="mt-16 text-center">
          <p className="text-slate-600 mb-4">{t("landing.faq.stillQuestions")}</p>
          <a href="mailto:support@keiront.com" className="text-keiront-green font-semibold hover:underline">
            {t("landing.faq.emailTeam")}
          </a>
        </div>
      </div>
    </section>
  );
}
